import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../../services/platformAPI';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import { useMasterRevision } from '../../../components/workspace/useMasterRevision';
import { useAltF1TestFill } from '../../../hooks/useAltF1TestFill';
import { dummyParivartanRateForm } from '../../../utils/testFormDummies';
import './CustomerMaster.scss';
import './PersonnelPage.scss';

const EMPTY = {
  designation: '',
  designationCode: '',
  purpose: '',
  amount: '',
  rateBasis: 'hourly',
  effectiveFrom: new Date().toISOString().slice(0, 10),
  effectiveTo: '',
  isActive: true
};

const dateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const RateFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { rateId } = useParams();
  const user = useSelector((state) => state.auth.user);
  const isCreate = location.pathname.endsWith('/new');
  const isEdit = location.pathname.endsWith('/edit');
  const isView = !isCreate && !isEdit;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const revision = useMasterRevision({ entity: 'rate' });
  const [designations, setDesignations] = useState([]);

  const heading = isCreate ? 'New costing rate' : (form.designation || 'Costing rate');
  useDocumentTitle(heading);

  useEffect(() => {
    if (isCreate) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await platformAPI.getRate(rateId);
        const row = response.data?.data?.rate;
        if (!cancelled && row) {
          setForm({
            designation: row.designation || '',
            designationCode: row.designationCode || '',
            purpose: row.purpose && row.purpose !== 'unclassified' ? row.purpose : '',
            amount: row.amountPaise != null ? String(row.amountPaise / 100) : '',
            rateBasis: row.rateBasis || 'hourly',
            effectiveFrom: dateInput(row.effectiveFrom),
            effectiveTo: dateInput(row.effectiveTo),
            isActive: row.isActive !== false,
            revision: row.revision || 1
          });
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Unable to open this rate');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isCreate, rateId]);

  useEffect(() => {
    platformAPI.listReference('personnel-designations', { keep: form.designationCode || undefined })
      .then((response) => setDesignations(response.data?.data?.values || []))
      .catch(() => setDesignations([]));
  }, [form.designationCode]);

  useAltF1TestFill(isCreate, () => {
    const sample = dummyParivartanRateForm();
    setForm({
      designation: sample.designation,
      amount: sample.amountPaise,
      rateBasis: sample.rateBasis,
      effectiveFrom: sample.effectiveFrom,
      isActive: true
    });
  });

  const setField = (key) => (event) => {
    const value = key === 'isActive' ? event.target.value === 'active' : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
  };

  const save = async (event) => {
    event.preventDefault();
    const amount = Number(form.amount);
    if (!form.designation.trim() || !Number.isFinite(amount) || amount <= 0) {
      setError('Enter a designation and an amount in rupees.');
      return;
    }
    if (isCreate && !form.purpose) {
      setError('Choose internal effort cost or charge-out.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      let changeReason;
      if (!isCreate) {
        changeReason = await revision.askReason(form.revision);
        if (!changeReason) {
          setSaving(false);
          return;
        }
      }
      const response = await platformAPI.upsertRate({
        id: isCreate ? undefined : rateId,
        designation: form.designation.trim(),
        designationCode: form.designationCode || undefined,
        purpose: form.purpose || undefined,
        amountPaise: Math.round(amount * 100),
        rateBasis: form.rateBasis,
        effectiveFrom: form.effectiveFrom,
        effectiveTo: form.effectiveTo || null,
        isActive: form.isActive,
        changeReason
      });
      const id = response.data?.data?.rate?._id;
      if (isCreate && id) {
        navigate(`/apps/tender/masters/rates/${id}/edit`);
      } else {
        navigate('/apps/tender/masters/rates');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'The rate could not be saved');
      setSaving(false);
    }
  };

  if (!canManageMasters(user?.permissions, user?.roles)) {
    return <Navigate to="/apps/tender" replace />;
  }

  if (loading) return <Spinner animation="border" size="sm" />;

  const locked = isView;
  const basisLabel = form.rateBasis === 'monthly' ? 'Monthly' : 'Hourly';

  return (
    <div className="cust-page">
      <form className={`cust-record pers-record${locked ? ' cust-record--view' : ''}`} onSubmit={save}>
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/masters/rates')}>
              <BiArrowBack />
              Costing Rates
            </button>
            <div className="cust-mast__row">
              <span className="cust-mark" aria-hidden>₹</span>
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">{isCreate ? 'Rate master' : isEdit ? 'Editing rate' : 'Rate master'}</p>
                <h1>{heading}</h1>
                <div className="cust-mast__meta">
                  <span className="cust-chip">{basisLabel}</span>
                  <span className={`cust-status cust-status--${form.isActive ? 'active' : 'inactive'}`}>
                    {form.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="cust-mast__actions">
            {isView ? (
              <Button size="sm" variant="primary" onClick={() => navigate(`/apps/tender/masters/rates/${rateId}/edit`)}>
                Edit
              </Button>
            ) : (
              <>
                <Button size="sm" variant="outline-primary" type="button" onClick={() => navigate('/apps/tender/masters/rates')} disabled={saving}>
                  Cancel
                </Button>
                <Button size="sm" variant="primary" type="submit" disabled={saving || !form.designation.trim()}>
                  {saving ? 'Saving…' : isCreate ? 'Create rate' : 'Save changes'}
                </Button>
              </>
            )}
          </div>
        </header>

        {error ? <Alert variant="danger" className="cust-record__alert">{error}</Alert> : null}

        <div className="cust-record__body">
          <div className="cust-record__main">
            <section className="cust-section">
              <header className="cust-section__head">
                <span>01</span>
                <div>
                  <h2>Costing rate</h2>
                  <p>Internal effort cost prices approved hours. Charge-out is proposed client pricing. An unclassified rate stays out of both calculations.</p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="Purpose" wide="8">
                  <Form.Select value={form.purpose} onChange={setField('purpose')} disabled={locked} aria-label="Rate purpose">
                    <option value="">{isCreate ? 'Select a purpose' : 'Needs classification'}</option>
                    <option value="effort_cost">Internal effort cost</option>
                    <option value="charge_out">Charge-out</option>
                  </Form.Select>
                </Field>
                <Field label="Designation" wide="8">
                  <Form.Select
                    value={form.designationCode}
                    disabled={locked}
                    onChange={(event) => {
                      const code = event.target.value;
                      const match = designations.find((row) => row.code === code);
                      setForm((current) => ({
                        ...current,
                        designationCode: code,
                        designation: match && code !== 'other' ? match.label : current.designation
                      }));
                    }}
                  >
                    <option value="">{form.designation || 'Select a designation'}</option>
                    {designations.map((row) => <option key={row.code} value={row.code}>{row.label}{row.status === 'inactive' ? ' (inactive)' : ''}</option>)}
                  </Form.Select>
                  {!form.designationCode && form.designation ? <div className="form-text">Stored as “{form.designation}”. It is not matched to a designation until you choose one.</div> : null}
                </Field>
                {form.designationCode === 'other' || !form.designationCode ? (
                  <Field label="Designation text" wide="8">
                    <Form.Control value={form.designation} onChange={setField('designation')} required disabled={locked} placeholder="Resident Engineer" />
                  </Field>
                ) : null}
                <Field label="Status" wide="4">
                  <Form.Select value={form.isActive ? 'active' : 'inactive'} onChange={setField('isActive')} disabled={locked}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Field>
                <Field label="Amount (INR)" wide="4">
                  <Form.Control type="number" min="1" step="1" value={form.amount} onChange={setField('amount')} required disabled={locked} placeholder="1500" />
                </Field>
                <Field label="Basis" wide="4">
                  <Form.Select value={form.rateBasis} onChange={setField('rateBasis')} disabled={locked}>
                    <option value="hourly">Hourly</option>
                    <option value="monthly">Monthly</option>
                  </Form.Select>
                </Field>
                <Field label="Effective from" wide="4">
                  <Form.Control type="date" value={form.effectiveFrom} onChange={setField('effectiveFrom')} required disabled={locked} />
                </Field>
                <Field label="Effective to" wide="4">
                  <Form.Control type="date" value={form.effectiveTo} onChange={setField('effectiveTo')} disabled={locked} />
                </Field>
              </div>
            </section>
          </div>
        </div>
      </form>
      {revision.modals}
    </div>
  );
};

export default RateFormPage;
